import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';
import {
  getQueryAutomationQueryKey,
  useQueryAutomation,
} from '@/generated/hooks/grimoire/v1/api';

/**
 * Hook to determine if a draft automation can be published.
 *
 * A draft can be published when the latest draft version is known and the
 * draft actually has SPy code. Grimoire rejects `:validatePublish` and
 * `:publish` with `400 Automation code is empty` for a code-less draft, so
 * gating on `code_length` here keeps the UI from offering a publish that the
 * backend is guaranteed to refuse (SEN-106 / SEN-107).
 *
 * @param currentOrganizationId - Organization ID
 * @param currentWorkspaceId - Workspace ID
 * @param draftId - Draft automation ID
 * @param enabled - Whether to enable React Query fetching (default: true)
 * @returns Object with canPublish flag, loading and error states, and draft data
 */
function useCanPublish(
  currentOrganizationId?: string,
  currentWorkspaceId?: string,
  draftId?: string,
  enabled: boolean = true,
) {
  const canFetch = Boolean(
    enabled && draftId && currentOrganizationId && currentWorkspaceId,
  );

  // Fetch draft automation to get version
  const {
    data: latestDraftData,
    isLoading: isLatestDraftLoading,
    isFetching: isLatestDraftFetching,
    isError: isLatestDraftError,
  } = useQueryAutomation(
    currentOrganizationId || '',
    currentWorkspaceId || '',
    draftId || '',
    { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
    {
      query: {
        refetchOnMount: 'always',
        enabled: canFetch,
      },
    },
  );

  const draftVersion = latestDraftData?.data?.version;

  // Same signal the editor uses to gate Run/Test — the length of the compiled
  // SPy code, which is what the publish endpoints validate against.
  const hasCode = (latestDraftData?.data?.code_length ?? 0) > 0;

  const canPublish = useMemo(() => {
    return Boolean(draftVersion) && hasCode;
  }, [draftVersion, hasCode]);

  const queryClient = useQueryClient();

  // eslint-disable-next-line no-restricted-syntax -- reason: Invalidate stale cache when disabled with canPublish=false, so next menu open refetches fresh data
  useEffect(() => {
    if (!enabled && !canPublish) {
      queryClient.invalidateQueries({
        queryKey: getQueryAutomationQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          draftId,
          { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
        ),
      });
    }
  }, [
    enabled,
    canPublish,
    queryClient,
    currentOrganizationId,
    currentWorkspaceId,
    draftId,
  ]);

  return {
    isLoading: isLatestDraftLoading || isLatestDraftFetching,
    isError: isLatestDraftError,
    canPublish,
    hasCode,
    draftVersion,
    displayName: latestDraftData?.data?.display_name || '',
    hasArtifact: Boolean(latestDraftData?.data?.english_code),
    hasPublishedVersion: Boolean(
      latestDraftData?.data?.latest_published_version,
    ),
  };
}

export default useCanPublish;
